import React, { FC, useMemo, useState } from 'react';

import {
  Box,
  ButtonProps,
  Checkbox,
  checkboxClasses,
  CircularProgress,
  Divider,
  Grid,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { GearSix } from 'phosphor-react';

import { useGraphStatus } from '~components/hooks/useGraphStatus';
import { useAppSettings } from '~components/providers/SettingsProvider';
import { FullWidthDivider } from '~components/shared/divider';
import AppColors, { primaryColors, statusOrange } from '~components/theme/styles/colors';
import { columnGap } from '~components/theme/styles/mixins';

import { SmolButton } from '../../buttons/smolButton';

import { AccountPanel } from './accountPanel';

type FetchTypes = 'chain' | 'graph';

const DataFetchingSettings: FC = () => {
  const settings = useAppSettings();
  const { data: isGraphFetchable, isFetching } = useGraphStatus();
  const overrideAutoCheck = useMemo(
    () => settings.getSetting('fetchingOverride') !== false,
    [settings.settings],
  );
  const [overrideValue, setOverrideValue] = useState<FetchTypes>(settings.getSetting('fetchingOverride'));
  const fetchFromGraph = useMemo(
    () =>
      settings.getSetting('fetchingOverride') === 'graph' ||
      settings.getSetting('fetchingOverride') === false,
    [settings.settings],
  );

  function getDataFetchingStatus() {
    if (isGraphFetchable) {
      if (overrideAutoCheck) {
        return 'Forcing Subgraph';
      }

      return 'Subgraph OK';
    } else {
      if (overrideAutoCheck) {
        return 'Forcing on-chain';
      }

      return 'Falling back On-chain';
    }
  }

  function setOverride(override: boolean) {
    settings.saveSettings(
      { fetchingOverride: override ? (fetchFromGraph ? 'graph' : 'chain') : false },
      true,
    );
  }

  function setOverrideMode(fetchGraph: boolean) {
    if (!overrideAutoCheck) return;
    setOverrideValue(fetchGraph ? 'graph' : 'chain');
    settings.saveSettings({ fetchingOverride: fetchGraph ? 'graph' : 'chain' }, true);
  }

  return (
    <Stack direction={'column'} spacing={2}>
      <Typography variant={'label3'} color={primaryColors[900]}>
        DATA FETCHING
      </Typography>
      <Stack direction={'column'}>
        <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between', height: '32px' }}>
          <Typography variant={'label3'} color={primaryColors[400]}>
            STATUS:
          </Typography>
          <Box component="span" sx={{ marginRight: '6px' }}>
            {isFetching ? <CircularProgress size={22} /> : null}
          </Box>
        </Grid>
        <Typography color={primaryColors[700]} variant={'label3'}>
          {getDataFetchingStatus()}
        </Typography>
      </Stack>
      <Divider sx={{ margin: 0 }} />

      <Stack>
        <Stack direction={'row'} alignItems={'center'} onClick={() => setOverride(!overrideAutoCheck)}>
          <Box mr={1}>
            <Checkbox
              disableTouchRipple
              disableRipple
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 16 },
                p: 0,
                color: primaryColors[400],
                [`&, &.${checkboxClasses.checked}`]: {
                  color: primaryColors[400],
                },
              }}
              checked={overrideAutoCheck}
            />
          </Box>
          <Typography variant={'label3'} sx={{ cursor: 'pointer' }}>
            AUTO-FETCH OVERRIDE
          </Typography>
        </Stack>
        {overrideAutoCheck && (
          <Stack direction={'column'}>
            <Stack direction={'row'} alignItems={'center'}>
              <Radio
                size={'small'}
                sx={{ '&, &.Mui-checked': { color: primaryColors[100] } }}
                checked={overrideValue === 'graph'}
                value={'graph'}
                onChange={e => {
                  if (e.target.value === 'graph') setOverrideMode(true);
                  setOverrideValue(e.target.value as FetchTypes);
                }}
              />
              <Typography color={AppColors.neutralGrey[3]} variant={'label3'}>
                FORCE SUBGRAPH FETCHING
              </Typography>
            </Stack>
            <Stack direction={'row'} alignItems={'center'}>
              <Radio
                size={'small'}
                sx={{ '&, &.Mui-checked': { color: primaryColors[100] } }}
                checked={overrideValue === 'chain'}
                value={'chain'}
                onChange={e => {
                  if (e.target.value === 'chain') setOverrideMode(false);
                  setOverrideValue(e.target.value as FetchTypes);
                }}
              />
              <Typography color={AppColors.neutralGrey[3]} variant={'label3'}>
                FORCE ON-CHAIN FETCHING
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>

      {!overrideAutoCheck && (
        <Stack>
          <Typography color={statusOrange[900]} variant={'body3'}>
            NOTE:
          </Typography>
          <Typography color={statusOrange[500]} variant={'body3'}>
            This setting is determined automatically. Overriding this setting is not recommended.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export const AccountDialSettings: FC<{
  forceOpen?: boolean;
  viewAllClick?: ButtonProps['onClick'];
}> = ({ forceOpen, viewAllClick }) => {
  const settings = useAppSettings();

  return (
    <AccountPanel
      titleContent="Quick Settings"
      forceOpen={forceOpen}
      titleIcon={<GearSix size={22} color={primaryColors[400]} />}
    >
      <Stack width={'100%'} direction={'column'} spacing={1}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}></Stack>
        <Stack sx={columnGap(16)}>
          <DataFetchingSettings />
          <FullWidthDivider offsetPadding={32} sx={{ margin: 0 }} />
          <Stack direction={'row'} gap="20px">
            <SmolButton
              variant="secondary"
              sx={{ color: primaryColors[400] }}
              onClick={() => settings.resetDefaults(true)}
            >
              RESET TO DEFAULTS
            </SmolButton>
            {!!viewAllClick && (
              <SmolButton variant="secondary" sx={{ color: primaryColors[400] }} onClick={viewAllClick}>
                VIEW ALL
              </SmolButton>
            )}
          </Stack>
        </Stack>
      </Stack>
    </AccountPanel>
  );
};
